import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Trash2, Pin, ArrowRightLeft, Filter as FilterIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * ThreadsRuangPanel — the enhanced "Thread / diskusi" tab.
 * Renders the existing threads list PLUS: a Ruang dropdown per row, filters
 * (kategori/ruang/belum-punya-ruang), a counter row, and a bulk "Pindahkan ke
 * Ruang" toolbar that appears when ≥1 thread is checked.
 *
 * Only `ruang_id` is ever written to a thread doc — every other field is
 * preserved end-to-end. Ruang list is fetched at mount from the live DB (no
 * hard-coded IDs) via GET /api/ruang.
 */
export default function ThreadsRuangPanel({ threads, catLabel, isAdminOrSuper, togglePin, deleteThread, onReload, initialFilterRuang, onFilterRuangConsumed }) {
  const [ruangList, setRuangList] = useState([]);
  const [ruangLoaded, setRuangLoaded] = useState(false);
  const [rowPending, setRowPending] = useState(null); // thread_id currently mid-request
  const [selected, setSelected] = useState(() => new Set()); // thread_ids checked
  const [filterCat, setFilterCat] = useState("");
  const [filterRuang, setFilterRuang] = useState(initialFilterRuang || "");
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);
  const [bulkTarget, setBulkTarget] = useState(""); // ruang_id selected in bulk dropdown
  const [bulkBusy, setBulkBusy] = useState(false);

  // Deep-link handoff: when RuangAdminPanel opens this tab with a ruang_id, update
  // the filter so admin lands directly on the discussions blocking deactivate.
  useEffect(() => {
    if (initialFilterRuang) {
      setFilterRuang(initialFilterRuang);
      onFilterRuangConsumed && onFilterRuangConsumed();
    }
  }, [initialFilterRuang, onFilterRuangConsumed]);

  useEffect(() => {
    api.get("/ruang").then((r) => {
      setRuangList(r.data?.all || []);
      setRuangLoaded(true);
    }).catch(() => setRuangLoaded(true));
  }, []);

  // ------ Filter + counter derived state ------
  const filtered = useMemo(() => {
    return threads.filter((t) => {
      if (filterCat && t.category !== filterCat) return false;
      if (filterRuang && t.ruang_id !== filterRuang) return false;
      if (onlyUnassigned && t.ruang_id) return false;
      return true;
    });
  }, [threads, filterCat, filterRuang, onlyUnassigned]);

  const counters = useMemo(() => {
    const total = threads.length;
    const withRuang = threads.filter((t) => !!t.ruang_id).length;
    return { total, withRuang, without: total - withRuang };
  }, [threads]);

  const categoriesSet = useMemo(() => {
    const s = new Set(threads.map((t) => t.category).filter(Boolean));
    return Array.from(s).sort();
  }, [threads]);

  // ------ Selection helpers ------
  const toggleOne = (tid) => {
    setSelected((prev) => {
      const nx = new Set(prev);
      nx.has(tid) ? nx.delete(tid) : nx.add(tid);
      return nx;
    });
  };
  const allFilteredIds = useMemo(() => filtered.map((t) => t.thread_id), [filtered]);
  const allFilteredChecked = allFilteredIds.length > 0 && allFilteredIds.every((id) => selected.has(id));
  const toggleAllFiltered = () => {
    if (allFilteredChecked) {
      setSelected((prev) => {
        const nx = new Set(prev);
        allFilteredIds.forEach((id) => nx.delete(id));
        return nx;
      });
    } else {
      setSelected((prev) => {
        const nx = new Set(prev);
        allFilteredIds.forEach((id) => nx.add(id));
        return nx;
      });
    }
  };

  // ------ Actions ------
  const assignRow = async (t, ruang_id) => {
    if (rowPending) return;
    setRowPending(t.thread_id);
    try {
      await api.patch(`/admin/threads/${t.thread_id}/ruang`, { ruang_id: ruang_id || null });
      // Mutate the local copy of the row so the dropdown reflects the new value
      // without waiting for the parent reload — parent reload still runs for consistency.
      t.ruang_id = ruang_id || null;
      toast.success(ruang_id ? "Ruang diperbarui" : "Assignment dibersihkan");
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memperbarui Ruang");
    } finally { setRowPending(null); }
  };

  const runBulk = async () => {
    if (!selected.size) return;
    const label = bulkTarget
      ? (ruangList.find((r) => r.ruang_id === bulkTarget)?.name || "Ruang terpilih")
      : "Belum ditentukan (kosongkan)";
    if (!window.confirm(`Pindahkan ${selected.size} thread ke "${label}"?\n\nHanya field ruang_id yang berubah. Judul, penulis, timestamp, dan balasan tetap.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/threads/bulk-ruang", {
        thread_ids: [...selected], ruang_id: bulkTarget || null,
      });
      toast.success(`${r.data.modified} thread dipindahkan`);
      setSelected(new Set());
      setBulkTarget("");
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk assign gagal");
    } finally { setBulkBusy(false); }
  };

  const ruangLabel = (id) => ruangList.find((r) => r.ruang_id === id)?.name || id;

  return (
    <div data-testid="community-threads-panel" className="space-y-3">
      {/* Counter */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-[#1F2937]" data-testid="threads-counter">
          <strong>{counters.total}</strong> Thread ·{" "}
          <span className="text-emerald-700">{counters.withRuang} sudah memiliki Ruang</span> ·{" "}
          <span className="text-[#B45309]">{counters.without} belum memiliki Ruang</span>
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-[#F3F1ED] border border-[#EAE7E0] p-3">
        <FilterIcon size={14} className="text-[#4B5563] shrink-0" />
        <label className="text-xs text-[#4B5563]">Kategori</label>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="filter-cat" className="h-8 rounded-full border border-[#EAE7E0] bg-white text-xs px-2">
          <option value="">Semua</option>
          {categoriesSet.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
        </select>
        <label className="text-xs text-[#4B5563] ml-2">Ruang</label>
        <select value={filterRuang} onChange={(e) => setFilterRuang(e.target.value)} data-testid="filter-ruang" className="h-8 rounded-full border border-[#EAE7E0] bg-white text-xs px-2">
          <option value="">Semua</option>
          {ruangList.map((r) => <option key={r.ruang_id} value={r.ruang_id}>{r.icon} {r.name}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs text-[#4B5563] ml-2 cursor-pointer">
          <input type="checkbox" checked={onlyUnassigned} onChange={(e) => setOnlyUnassigned(e.target.checked)} data-testid="filter-unassigned" />
          Hanya belum memiliki Ruang
        </label>
        {(filterCat || filterRuang || onlyUnassigned) && (
          <button
            onClick={() => { setFilterCat(""); setFilterRuang(""); setOnlyUnassigned(false); }}
            className="ml-auto text-xs text-[#E07A5F] hover:underline"
            data-testid="filter-clear"
          >
            Bersihkan filter
          </button>
        )}
      </div>

      {/* Bulk action toolbar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-2xl bg-[#EEF2FF] border border-[#c9d4e4] p-3" data-testid="bulk-toolbar">
          <span className="text-sm text-[#1E3A8A] font-medium">{selected.size} dipilih</span>
          <ArrowRightLeft size={14} className="text-[#4B5563]" />
          <select value={bulkTarget} onChange={(e) => setBulkTarget(e.target.value)} data-testid="bulk-ruang-select" className="h-8 rounded-full border border-[#c9d4e4] bg-white text-xs px-2">
            <option value="">Belum ditentukan (kosongkan)</option>
            {ruangList.map((r) => <option key={r.ruang_id} value={r.ruang_id}>{r.icon} {r.name}</option>)}
          </select>
          <Button onClick={runBulk} disabled={bulkBusy} data-testid="bulk-apply" className="rounded-full bg-[#1E3A8A] hover:bg-[#14294D] text-white h-8">
            {bulkBusy ? "Memproses…" : "Pindahkan ke Ruang"}
          </Button>
          <button onClick={() => setSelected(new Set())} data-testid="bulk-clear" className="ml-auto text-xs text-[#4B5563] hover:underline">Batal</button>
        </div>
      )}

      <p className="text-xs text-[#4B5563]">Assignment hanya menulis <code>ruang_id</code>. Field lain (judul, kategori lama, balasan, dsb) tidak berubah dan bisa diassign ulang kapan saja.</p>

      <div className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F1ED] text-xs uppercase tracking-widest text-[#4B5563]">
              <tr>
                <th className="p-3 w-10 text-left">
                  <input type="checkbox" checked={allFilteredChecked} onChange={toggleAllFiltered} data-testid="select-all" aria-label="Pilih semua di halaman" />
                </th>
                <th className="text-left p-3">Thread</th>
                <th className="text-left p-3 w-36">Kategori</th>
                <th className="text-left p-3 w-52">Ruang</th>
                <th className="text-left p-3 w-36">Penulis</th>
                <th className="text-right p-3 w-16">Balasan</th>
                <th className="text-right p-3 w-40">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => (
                <tr key={t.thread_id} data-testid={`thread-row-${i}`} className="border-t border-[#EAE7E0]">
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selected.has(t.thread_id)}
                      onChange={() => toggleOne(t.thread_id)}
                      data-testid={`row-check-${t.thread_id}`}
                      aria-label="Pilih thread"
                    />
                  </td>
                  <td className="p-3">
                    <div className="flex items-start gap-2">
                      {t.pinned && <Pin size={12} className="text-[#9A3412] mt-1 shrink-0" />}
                      <div className="min-w-0">
                        <a href={`/forum/legacy/${t.thread_id}`} target="_blank" rel="noreferrer" className="text-[#1F2937] hover:text-[#E07A5F] font-medium line-clamp-1">{t.title}</a>
                        <div className="text-xs text-[#4B5563] mt-0.5">{new Date(t.created_at).toLocaleDateString("id-ID")}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-[#4B5563]">{catLabel(t.category)}</td>
                  <td className="p-3">
                    <select
                      value={t.ruang_id || ""}
                      onChange={(e) => assignRow(t, e.target.value)}
                      disabled={!ruangLoaded || rowPending === t.thread_id}
                      data-testid={`row-ruang-${t.thread_id}`}
                      className={`h-8 rounded-full border text-xs px-2 max-w-[200px] w-full bg-white transition-colors ${
                        t.ruang_id ? "border-emerald-300 text-[#065F46]" : "border-[#E5E7EB] text-[#B45309]"
                      } disabled:opacity-60`}
                    >
                      <option value="">Belum ditentukan</option>
                      {ruangList.map((r) => (
                        <option key={r.ruang_id} value={r.ruang_id}>{r.icon} {r.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-3 text-[#4B5563]">{t.user_name}</td>
                  <td className="p-3 text-right text-[#4B5563]">{t.reply_count}</td>
                  <td className="p-3 text-right">
                    {isAdminOrSuper && (
                      <Button variant="outline" onClick={() => togglePin(t)} data-testid={`pin-thread-${i}`} className={"rounded-full h-8 mr-2 " + (t.pinned ? "border-[#9A3412] text-[#9A3412]" : "border-[#EAE7E0]")}>
                        <Pin size={12} />
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => deleteThread(t)} data-testid={`delete-thread-${i}`} className="rounded-full border-[#EAE7E0] text-[#B91C1C] h-8">
                      <Trash2 size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-[#4B5563]">Tidak ada thread yang cocok dengan filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Named helper — parent may want to render the current label somewhere else.
export function useRuangLabel() {
  const [ruangList, setRuangList] = useState([]);
  useEffect(() => { api.get("/ruang").then((r) => setRuangList(r.data?.all || [])).catch(() => {}); }, []);
  return (id) => ruangList.find((r) => r.ruang_id === id)?.name || id || "—";
}
